import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import IntegrationCard from '@/components/integrations/IntegrationCard';
import {
  connectQuickBooks,
  disconnectQuickBooks,
  getQuickBooksConnectionStatus,
} from '@/services/quickbooksService';

export default function IntegrationsSettings() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [quickBooksConnected, setQuickBooksConnected] = useState(false);

  const loadStatus = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const result = await getQuickBooksConnectionStatus();
      setQuickBooksConnected(!!result?.connected);
    } catch (err) {
      console.error('[IntegrationsSettings] Failed to load QuickBooks status:', err);
      setError(err.message || 'Failed to load integration status');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) {
      loadStatus();
    } else if (!authLoading && !user) {
      navigate('/login');
    }
  }, [authLoading, user, loadStatus, navigate]);

  useEffect(() => {
    const successParam = searchParams.get('success');
    const errorParam = searchParams.get('error');

    if (successParam === 'quickbooks_connected') {
      setSuccess('QuickBooks connected successfully.');
    }

    if (errorParam) {
      setError('QuickBooks connection was not completed. Please try again.');
    }
  }, [searchParams]);

  const handleConnectQuickBooks = async () => {
    try {
      setConnecting(true);
      setError(null);
      setSuccess(null);

      const result = await connectQuickBooks();

      if (!result?.authUrl) {
        throw new Error('Could not start QuickBooks OAuth flow');
      }

      window.location.assign(result.authUrl);
    } catch (err) {
      console.error('[IntegrationsSettings] Connect error:', err);
      setError(err.message || 'Failed to connect QuickBooks');
      setConnecting(false);
    }
  };

  const handleDisconnectQuickBooks = async () => {
    if (!window.confirm('Disconnect QuickBooks from this GreenQuote account?')) {
      return;
    }

    try {
      setDisconnecting(true);
      setError(null);
      setSuccess(null);

      await disconnectQuickBooks();
      setQuickBooksConnected(false);
      setSuccess('QuickBooks disconnected successfully.');
    } catch (err) {
      console.error('[IntegrationsSettings] Disconnect error:', err);
      setError(err.message || 'Failed to disconnect QuickBooks');
    } finally {
      setDisconnecting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-xl font-bold text-green-600">
                🌱 GreenQuote Pro
              </Link>
            </div>
            <nav className="flex items-center gap-4">
              <Link to="/settings" className="text-gray-600 hover:text-gray-900">
                ← Back to Settings
              </Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-gray-600 mt-1">Connect GreenQuote to the tools you already use.</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-6">
          <IntegrationCard
            title="QuickBooks Online"
            description="Sync customers and invoices with QuickBooks Online"
            connected={quickBooksConnected}
            connecting={connecting}
            disconnecting={disconnecting}
            onConnect={handleConnectQuickBooks}
            onDisconnect={handleDisconnectQuickBooks}
          />
        </div>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/settings')}
            className="border-green-600 text-green-600 hover:bg-green-50"
          >
            ← Back to Settings
          </Button>
        </div>
      </main>
    </div>
  );
}
